/**
 * Pure H OS · Phase D Wave 3 · Unified Risk Register.
 * Every open / monitoring / mitigated risk across the platform.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge, Button } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { LinkedRefChip } from "../components/LinkedRefChip";
import { RISKS_REGISTRY, RiskEntry, RiskStatus } from "../mocks/risksRegistryData";
import { api } from "../services/api";
import { upsertLocal } from "../services/store";
import { useLiveTick } from "../lib/useLiveTick";

const STATUS_LABEL: Record<RiskStatus, string> = {
  open: "Open", monitoring: "Monitoring", mitigated: "Mitigated",
};

type Tab = "all" | "high" | RiskStatus;

export default function RiskRegister() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<RiskStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useLiveTick();

  async function applyStatus(status: RiskStatus) {
    if (!openId) return;
    setBusy(status); setErr(null);
    try {
      const updated = await api.patch("risks_registry", openId, { status }) as Partial<RiskEntry> & { id: string };
      upsertLocal("risks_registry", updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Update failed");
    } finally { setBusy(null); }
  }

  const filtered = useMemo(() => {
    if (tab === "all") return RISKS_REGISTRY;
    if (tab === "high") return RISKS_REGISTRY.filter((r) => r.severity === "high");
    return RISKS_REGISTRY.filter((r) => r.status === tab);
  }, [tab]);

  const current = openId ? RISKS_REGISTRY.find((r) => r.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="risks-page">
      <Card testId="rr-filters">
        <TabBar<Tab>
          testId="rr-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",         label: `All ${RISKS_REGISTRY.length}` },
            { key: "high",        label: "High" },
            { key: "open",        label: "Open" },
            { key: "monitoring",  label: "Monitoring" },
            { key: "mitigated",   label: "Mitigated" },
          ]}
        />
      </Card>

      <Card padded={false} testId="rr-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Unified Risk Register · canonical" title={`${filtered.length} risk${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>The single source of truth for risk across finance, ops, R&D, launch, marketing and executive. Module-level risk tabs (Finance Risks, Operations Risks) are filtered views onto this register.</div>
        </div>
        <div className="p-2">
          {filtered.map((r) => (
            <ListRow key={r.id} testId={`rr-${r.id}`}
              onClick={() => setOpenId(r.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{r.module}</span>
                  <span>{r.title}</span>
                </span>
              }
              sublabel={<>{r.owner} · {STATUS_LABEL[r.status]} · updated {r.updatedRelative}</>}
              trailing={<SeverityBadge severity={r.severity} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="rr-drawer" title={current ? current.title : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${current.module} · ${STATUS_LABEL[current.status]}`} title={current.owner} />
            <div className="text-sm leading-relaxed" style={{ color: "var(--ph-text)" }}>{current.mitigation}</div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Severity</div>
                <div className="mt-2"><SeverityBadge severity={current.severity} /></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Likelihood</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.likelihood}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Impact</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.impact}</div>
              </div>
            </div>
            {(current as RiskEntry & { mitigatedByDecisionId?: string }).mitigatedByDecisionId && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Mitigated by</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>
                  <LinkedRefChip
                    id={(current as RiskEntry & { mitigatedByDecisionId?: string }).mitigatedByDecisionId}
                    kind="decision"
                    testId={`rr-mitigated-${current.id}`} />
                </div>
              </div>
            )}
            <div className="pt-4" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
              <div className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: "var(--ph-muted)" }}>
                Risk actions
              </div>
              {err && <div className="text-xs mb-2" style={{ color: "var(--ph-danger)" }} data-testid="rr-action-error">{err}</div>}
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyStatus("open")} testId="rr-action-open">
                  {busy === "open" ? "Reopening…" : "Reopen"}
                </Button>
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyStatus("monitoring")} testId="rr-action-monitor">
                  {busy === "monitoring" ? "Marking…" : "Monitor"}
                </Button>
                <Button variant="primary" disabled={busy !== null} onClick={() => applyStatus("mitigated")} testId="rr-action-mitigate">
                  {busy === "mitigated" ? "Mitigating…" : "Mitigate"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
